import { useEffect, useState, useRef } from 'react';
import { usePerformanceSentinel } from '../hooks/usePerformanceSentinel';
import { useVisibilitySentinel } from '../hooks/useVisibilitySentinel';

export interface RotatingHeadlineProps {
    /** Array of text strings to rotate through */
    texts: string[];
    /** Interval between text changes in milliseconds */
    intervalMs?: number;
    /** Additional CSS classes */
    className?: string;
}

/**
 * RotatingHeadline component that cycles through an array of text strings
 * with a fade animation.
 * Optimized with Performance and Visibility sentinels.
 */
export function RotatingHeadline({
    texts,
    intervalMs = 3000,
    className = '',
}: RotatingHeadlineProps) {
    const [index, setIndex] = useState(0);
    const [visible, setVisible] = useState(true);
    const containerRef = useRef<HTMLParagraphElement>(null);
    const { isPanic } = usePerformanceSentinel();
    const { isVisible } = useVisibilitySentinel(containerRef);

    useEffect(() => {
        // If performance is bad or not visible, do not start/continue rotation
        // and ensure we show the first text as the fallback
        if (isPanic || !isVisible) {
            setIndex(0);
            setVisible(true);
            return;
        }

        let mounted = true;

        const tick = () => {
            if (!mounted) return;
            setVisible(false);
            setTimeout(() => {
                if (!mounted) return;
                setIndex((i) => (i + 1) % texts.length);
                setVisible(true);
            }, 700); // Wait for fade out to complete
        };

        const id = setInterval(tick, intervalMs);
        return () => {
            mounted = false;
            clearInterval(id);
        };
    }, [intervalMs, texts.length, isPanic, isVisible]);

    return (
        <p
            ref={containerRef}
            className={`transition-all duration-700 ease-in-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                } ${className}`}
        >
            {texts[index]}
        </p>
    );
}
